import { useUserAccess } from '@Hooks/useUserAccess';
import service from '@Service';
import { getHomePath } from '@Utils/homeRoute';
import { oneSignalLogin, oneSignalLogout } from '@Utils/oneSignal';
import { User, UserAccess } from '@Types/user';
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DEFAULT_ROLES = {
  ADMIN_ROLE: '7eee5d0c-3172-4fad-bea0-1d831afb6549',
  ADMIN_VIEWER_ROLE: '805AC0D3-D837-48C0-BFB3-24AE9CBDFA9B',

  All_INBOXES: '66666666-6666-6666-6666-666666666666',
};

const DEFAULT_PERMISSIONS: Record<string, string> = {
  MEMBERS_READ: 'E4009BDB-53C6-42D5-B68E-6C2193BEE1E3',
  MEMBERS_WRITE: 'AF23B8DA-4C8C-4AA6-8E73-DC7ABA023059',
  TEAM_MEMBERS_READ: '3f8e2a9b-7c4d-4bfa-9d12-6f0b9a4d8c1e',
  TEAM_MEMBERS_WRITE: 'b2d7f9c3-5e41-4a7f-8d2e-3c9f7b6e1a0f',
  INSIGHTS_READ: 'D2DAC2E0-B5A0-4DF2-BF3E-49575C114D8A',
  INSIGHTS_WRITE: 'E8FCE33B-9378-4816-8E2D-137C65C4DC17',
  GUESTS_READ: 'B9A32984-2621-4666-A30F-52A1A5352D22',
  GUESTS_WRITE: '75F3C96E-1189-452D-88E7-4DB54607F201',
  TAGS_READ: 'A9A400AA-18A3-4231-B704-EF64128EF5C6',
  TAGS_WRITE: '45FCA90F-F79F-4378-9F0C-A64A71B6B59C',
  SEGMENTS_READ: '41D4C1C1-D33A-478A-A014-1673402FABDB',
  SEGMENTS_WRITE: '3C2E0E50-E5A0-4173-A88D-C71B3BEF40F2',
  INBOX_READ: '56925BD6-B7C8-418A-81B5-330DA937357D',
  INBOX_WRITE: 'A206309A-4D5A-4C6C-AE82-748CD08E7BCE',
  NUMBER_READ: '7f3a1c20-0000-4000-8000-000000000101',
  NUMBER_WRITE: '7f3a1c20-0000-4000-8000-000000000102',
  INBOX_MANAGE: '7f3a1c20-0000-4000-8000-000000000103',
  CAMPAIGNS_READ: '8E7A8F53-7899-41C0-82CA-CB7943827DFA',
  CAMPAIGNS_WRITE: '3701F2D0-DAF0-4EC4-B9C0-EDBA0CF1B388',
  MESSAGE_TEMPLATES_READ: '83F81306-102C-409C-883B-C4988FEA8B7F',
  MESSAGE_TEMPLATES_WRITE: '36B5C8FB-58FD-4DD7-AF30-AD86E40A4C93',
  EVENTS_READ: '30F977B3-1C4E-40D1-B3A0-913B4E53FB07',
  EVENTS_WRITE: 'E578C636-031C-4D05-A114-04213FA891E0',
  VENUES_READ: '31144015-D3A9-440D-9998-298E3E1D3866',
  VENUES_WRITE: '9F4B3886-E975-47AB-BE90-1D9941486F59',
  SPACES_READ: '934973E9-D780-4F0E-A79B-593522008E73',
  SPACES_WRITE: 'D2E3A4F7-DB09-40ED-8D2E-2FD53D2CEE9C',
  SHOP_READ: 'F316CF97-CBDF-47AF-B015-77CC09F64286',
  SHOP_WRITE: '47E742A4-767B-414D-BC2F-EBB965608BF7',
  DASHBOARD_READ: '5D67C165-5514-4B79-B003-6C8E2E0A0A56',
  DASHBOARD_WRITE: 'BA6B1656-DEFB-4ADD-A0B2-2F10E4EDD3A1',
  WALLET_READ: '51582346-63A0-4D1A-A1CB-25F6CCB4218A',
  WALLET_WRITE: '2E3DFE14-A9CE-4F4C-BB0C-E3752D15FD1B',
  ACCESS_OMNI_AGENT: '7EBBE7ED-CCD9-4E70-8432-CFF801F64C1D',

  TRIGGER_READ: '02275BD7-1D8C-470B-B4E6-7CB2A8B2C651',
  TRIGGER_WRITE: '129047A6-A7C8-4F18-A9BD-AE39BFD3181E',
  FLOW_READ: 'CB2D421E-49F1-4521-A949-6BED18C63C57',
  FLOW_WRITE: 'B61AE71A-1A35-4E8A-9800-252B133E6926',
  FLOW_BUILDER_READ: '36c3a238-279a-4580-a874-9c95a1a6acda',
  FLOW_BUILDER_WRITE: '36c3a238-279a-4580-a874-9c95a1a6acdc',
  PROMO_CODES_READ: '6bbe2450-875a-4158-8aa2-0ce5e89b9cab',
  PROMO_CODES_WRITE: 'f6502f1b-4e64-4a8e-87e2-260785341ebd',
  REGISTRATION_CODES_READ: 'c9a1d7f4-3e8b-4a6c-b5f2-1e9d6a0c8b7e',
  REGISTRATION_CODES_WRITE: '6b2f6e1e-6a5b-4f2c-9b6d-8e2f3a1c7d4a',
  FORM_READ: '59E7B8EE-E31E-4A91-B533-D20C3BE9CB6E',
  FORM_WRITE: 'AB03C75A-C52F-452E-8E61-922F684E03F5',
  USER_ROLES_READ: 'D63E8AD7-EEAA-4376-8497-A9C2678D006E',
  USER_ROLES_WRITE: '326D6492-4A26-46CE-A23C-35F58FAD585A',
  PURCHASES_READ: '33F74DFD-3283-4354-8085-955206A07796',
  PURCHASES_WRITE: 'A8D0476C-B6AB-4747-A2D3-89961A19935E',

  ASSETS_READ: '3AAD6EE4-72C4-4CDC-86D1-3AFBDDC2A0FF',
  ASSETS_WRITE: '3DDBF295-F3E1-47A3-B9BF-9CEC110F4AB3',
  SMS_READ: '0E597796-CE42-4D3C-AE17-6BF9EECA7D22',
  SMS_WRITE: '20E08149-2E9D-4A50-9EBA-65D9F40FDD40',
  USER_READ: '41259C6A-1E5E-42C6-A037-A44E3AC7C476',
  USER_WRITE: 'BF43076D-42A5-4736-A6DA-99F40CCF1DB9',
  FISH_READ: 'A1B6DA00-74B5-4F39-BCA8-F6D274462EE6',
  FISH_WRITE: 'EB52BB30-B6FE-4BE1-B908-C0D997367D26',
  LOCATIONS_READ: 'c6a6b6e1-8f24-4b2e-9b6e-6b9b8f7c3f41',
  LOCATIONS_WRITE: '3d9f2a0c-5e6b-4a77-bc2a-1f2d9e4a6c88',
  SUBSCRIPTION_READ: 'A9FCF7F6-741C-49EF-9A93-54CA40A4BB06',
  SUBSCRIPTION_WRITE: '5D4A4B07-D6C1-4BDC-B0C0-29B8E90A4A06',
  FISH_ACCESS_READ: 'ABE002DF-7116-4D3F-98ED-18FDFA79A391',
  FISH_ACCESS_WRITE: '1EA024A6-78E1-4C48-A0C5-D2AB00368AF6',
  USER_ACCESS_READ: '84095F30-E57D-46C1-B5E1-4AFDD3BC31C4',
  USER_ACCESS_WRITE: 'E5BF0DFD-352B-4CAE-BF8E-9F03495F3528',
  AI_ACCESS: 'F7A1B2C3-D4E5-6F78-9A0B-1C2D3E4F5A6B',
  ADMIN_ACCESS_ALL_ORGS: '47e5a1bb-abb6-4880-a62e-1523b22e7da6',
  CONTENT_READ: 'ABA28B5E-65C6-4278-958A-3FD1D2AA0757',
  CONTENT_WRITE: 'FD16D231-2D57-4EBD-A46E-7FD4BDECD6CC',
};

type AuthContextProps = {
  user: User;
  userAccessReady: boolean;
  isLoggedIn: boolean;
  sendOtp: (phoneNumber: string) => Promise<{ ok: boolean; message?: string }>;
  verifyOtp: (code: string) => Promise<{ ok: boolean; message?: string }>;
  resendOtp: () => Promise<boolean>;
  cancelOtp: () => void;
  pendingPhone: string | null;
  logout: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  hasRole: (roles: string[]) => boolean;
  hasPermission: (permissions: string[]) => boolean;
  checkAccess?: (
    roles: string[],
    permissions: string[],
    checkEveryRole?: boolean,
    checkEveryPermission?: boolean,
  ) => boolean;
};

const AuthContext = createContext<AuthContextProps | undefined>(undefined);
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  const { accessToken } = service.getAuthTokens();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const sendOtp = async (
    phoneNumber: string,
  ): Promise<{ ok: boolean; message?: string }> => {
    try {
      setIsLoading(true);
      const res = await service.sendOtp(phoneNumber);
      setIsLoading(false);
      if (!res.ok) {
        return { ok: false, message: 'Could not send code. Please try again.' };
      }
      setPendingPhone(phoneNumber);
      return { ok: true };
    } catch (error) {
      console.error('sendOtp failed:', error);
      setIsLoading(false);
      return { ok: false, message: 'An error occurred. Please try again.' };
    }
  };

  const verifyOtp = async (
    code: string,
  ): Promise<{ ok: boolean; message?: string }> => {
    if (!pendingPhone) {
      return { ok: false, message: 'No pending verification.' };
    }
    try {
      setIsLoading(true);
      const res = await service.verifyOtp(pendingPhone, code);

      if (!res.ok) {
        setIsLoading(false);
        return { ok: false, message: 'Invalid verification code.' };
      }

      const body = await res.json();
      const tokenData = body?.data;
      const matchedUser = body?.user;

      if (!tokenData?.accessToken || !matchedUser) {
        setIsLoading(false);
        return {
          ok: false,
          message: 'No admin account is associated with this phone number.',
        };
      }

      // Auth0 is configured with long-lived tokens for this app; the cookies
      // define the session length, not the token response's expiresIn.
      const SESSION_TTL_DAYS = 30;
      const expiresAt = new Date(
        Date.now() + SESSION_TTL_DAYS * 24 * 60 * 60 * 1000,
      );

      service.setAuthTokens({
        access: { token: tokenData.accessToken, expires: expiresAt },
        refresh: { token: tokenData.refreshToken, expires: expiresAt },
      });
      localStorage.setItem('user', JSON.stringify(matchedUser));
      setUser(matchedUser);
      setIsLoggedIn(true);
      oneSignalLogin(matchedUser.id).catch((e) =>
        console.error('OneSignal login failed', e),
      );
      setPendingPhone(null);
      navigate(getHomePath(), { replace: true });
      setIsLoading(false);
      return { ok: true };
    } catch (error) {
      console.error('verifyOtp failed:', error);
      setIsLoading(false);
      return { ok: false, message: 'An error occurred. Please try again.' };
    }
  };

  const resendOtp = async (): Promise<boolean> => {
    if (!pendingPhone) return false;
    try {
      const res = await service.sendOtp(pendingPhone);
      return res.ok;
    } catch (error) {
      console.error('resendOtp failed:', error);
      return false;
    }
  };

  const cancelOtp = () => {
    setPendingPhone(null);
  };

  const logout = () => {
    oneSignalLogout().catch((e) =>
      console.error('OneSignal logout failed', e),
    );
    service.clearAuthTokens();
    localStorage.removeItem('user');
    setUser(null);
    setIsLoggedIn(false);

    if (pathname !== '/login') {
      navigate(`/login`);
    }
  };

  const hasRole = (requiredRoles = [], checkEveryRole = false) => {
    if (!userAccess?.roles?.length) return false;

    if (checkEveryRole) {
      return requiredRoles.every((role) =>
        userAccess.roles.includes(DEFAULT_ROLES[role]),
      );
    } else {
      return requiredRoles.some((roleKey) =>
        userAccess.roles.includes(DEFAULT_ROLES[roleKey]),
      );
    }
  };

  const hasPermission = (
    requiredPermissions = [],
    checkEveryPermission = false,
  ) => {
    if (!userAccess?.permissions?.length) return false;

    if (checkEveryPermission) {
      return requiredPermissions.every((permission) =>
        userAccess.permissions.includes(DEFAULT_PERMISSIONS[permission]),
      );
    } else {
      return requiredPermissions.some((permission) =>
        userAccess.permissions.includes(DEFAULT_PERMISSIONS[permission]),
      );
    }
  };

  const checkAccess = (
    roles = [],
    permissions = [],
    checkEveryRole = false,
    checkEveryPermission = false,
  ) => {
    const roleAccess = roles.length === 0 || hasRole(roles, checkEveryRole);
    const permissionAccess =
      permissions.length === 0 ||
      hasPermission(permissions, checkEveryPermission);

    let allowed = false;

    if (checkEveryRole && checkEveryPermission) {
      allowed = roleAccess && permissionAccess;
    } else if (checkEveryRole) {
      allowed = roleAccess;
    } else if (checkEveryPermission) {
      allowed = permissionAccess;
    } else if (roles.length && permissions.length) {
      allowed = roleAccess || permissionAccess;
    } else {
      allowed = roles.length ? roleAccess : permissionAccess;
    }

    return allowed;
  };

  const { data: payload, isSuccess: userAccessReady } = useUserAccess({
    searchParams: {
      'include': 'userRole.userRolePermissions.permission',
      'filter[userId]': user?.id,
    },
    enabled: !!user?.id,
  });

  const userAccess: UserAccess | undefined = payload?.data;

  // Restores the session from storage when the access token appears or
  // changes. Navigation must not re-run this: `user` keeps its object identity
  // across URL changes, since effects elsewhere depend on it.
  useEffect(() => {
    setIsLoading(true);
    const cachedUser = localStorage.getItem('user');
    if (accessToken && cachedUser) {
      const parsedUser = JSON.parse(cachedUser);
      setUser(parsedUser);
      setIsLoggedIn(true);
      setIsLoading(false);
      oneSignalLogin(parsedUser.id).catch((e) =>
        console.error('OneSignal login failed', e),
      );
    } else {
      logout();
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken && pathname === '/login') {
      navigate(getHomePath(), { replace: true });
    } else if (!accessToken && pathname !== '/login') {
      navigate('/login');
    }
  }, [accessToken, pathname]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userAccessReady,
        isLoggedIn,
        sendOtp,
        verifyOtp,
        resendOtp,
        cancelOtp,
        pendingPhone,
        logout,
        isLoading,
        setIsLoading,
        hasRole,
        hasPermission,
        checkAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
